import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { withTheme } from "styled-components";

import { options } from "./options";
import { ConfirmationModal } from "shared";
import { trackEvent } from "utils/tracking";
import { Dropdown } from "components/Dropdown";
import { useNavigation } from "utils/navigate";
import { cloneStrategy } from "features/Strategy/service";
import { removeStrategy } from "features/Strategy/slice/thunk";
import { deleteIcon, MoreVertical } from "assets/icons";

const MoreStrategyAction = ({ strategyData = null, theme }) => {
  const dispatch = useDispatch();
  const {
    navigateToV2StrategyEdit,
    navigateToHostStrategy,
    navigateToV2EtfBuilder,
  } = useNavigation();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const strategyId = strategyData.strategy_id;
  const canEdit = strategyData.can_edit ?? true;
  const isDeployed = strategyData.deploy_status === 2;
  const notPublished = strategyData.published === 0;
  const showHostOption = isDeployed && notPublished;
  const isNoCodeStrategy = strategyData.type === "nocode";
  const isEtfStrategy = strategyData.type === "etf";

  const optionClickHandler = {
    clone: async () => {
      const response = await cloneStrategy({
        strategy_id: strategyId,
      });
      if (strategyData.type == "nocode") {
        navigateToV2EtfBuilder(response.strategy_id, {
          query: { builderType: "noCode" },
        });
      } else if (isEtfStrategy) {
        navigateToV2EtfBuilder(response.strategy_id);
      } else {
        navigateToV2StrategyEdit(response.strategy_id);
      }
      trackEvent({
        action: "submit",
        category: "strategy_clone",
        label: "Strategy",
      });
    },
    delete: async () => {
      toggleConfirmationModal();
    },
    edit: (id) => {
      if (isNoCodeStrategy) {
        navigateToV2EtfBuilder(id, { query: { builderType: "noCode" } });
      } else if (isEtfStrategy) {
        navigateToV2EtfBuilder(id);
      } else {
        navigateToV2StrategyEdit(id);
      }
      trackEvent({
        action: "submit",
        category: "strategy_edit",
        label: "Strategy",
      });
    },
    host: (id) => {
      navigateToHostStrategy(id);
    },
  };

  const toggleConfirmationModal = () => {
    setShowConfirmation(!showConfirmation);
  };

  const handleDelete = () => {
    trackEvent({
      action: "submit",
      category: "strategy_delete",
      label: "Strategy",
    });
    dispatch(removeStrategy(strategyId));
  };

  return (
    <>
      <Dropdown
        align="start"
        divider={false}
        disabled={!canEdit}
        title={<MoreVertical fill={theme.heading.color.secondary} />}
        onOptionClick={(option) => optionClickHandler[option.key](strategyId)}
        className={`strategy-action-dropdown ${!canEdit ? "no-edit" : ""}`}
        options={options(strategyData.published).filter((option) =>
          showHostOption ? option : option.key !== "host"
        )}
      />

      <ConfirmationModal
        title={`Are you sure you want to delete ${strategyData.name}?`}
        icon={deleteIcon}
        description="<p class='delete-strat-text'>This cannot be undone</p>"
        show={showConfirmation}
        onHide={toggleConfirmationModal}
        onConfirm={handleDelete}
        confirmButtonText="Delete Strategy"
        cancelButtonText="Cancel"
      />
    </>
  );
};

export default withTheme(MoreStrategyAction);

MoreStrategyAction.propTypes = {
  strategyData: PropTypes.object,
  theme: PropTypes.object,
};
